import { FC } from 'react';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Eye, X, Pencil, CheckCircle2, Calendar, Clock } from 'lucide-react';
import { EditableDataTable, EditableColumn, Dropdown, DropdownItem, Button, Select } from '@/components/ui';
import { SuggestedItem } from '../types';
import { EditContentModal } from './EditContentModal';

/**
 * Template Preview Component - Shows a creative preview of the template with title
 */
const TemplatePreview: FC<{ item: SuggestedItem }> = ({ item }) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Use renderImageUrl from item if available (from list API)
    if (item.renderImageUrl) {
      setPreviewImage(item.renderImageUrl);
      setHasError(false);
      setIsLoading(false);
    } else {
      // No render available yet
      setPreviewImage(null);
      setHasError(true);
      setIsLoading(false);
    }
  }, [item.renderImageUrl]);

  return (
    <div className="flex items-center gap-3 min-w-0 group">
      {/* Preview Thumbnail with Creative Design */}
      <div className="relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 border-gray-200 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
            <div className="flex flex-col items-center gap-1">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-600 border-t-transparent"></div>
              <div className="text-[8px] text-teal-600 font-medium">Loading...</div>
            </div>
          </div>
        ) : previewImage && !hasError ? (
          <>
            <img
              src={previewImage}
              alt="Template preview"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              onError={() => setHasError(true)}
            />
            {/* Gradient Overlay for better text visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
            <div className="text-center">
              <div className="text-lg font-bold text-teal-600 opacity-70">
                {item.template?.charAt(0).toUpperCase() || 'T'}
              </div>
              <div className="text-[8px] text-teal-500 font-medium mt-0.5">
                Preview
              </div>
            </div>
          </div>
        )}
        {/* Template Badge Overlay - Always visible for context */}
        {item.template && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent px-2 py-1">
            <span className="text-[9px] font-semibold text-white truncate block drop-shadow-sm">
              {item.template}
            </span>
          </div>
        )}
        {/* Corner accent for visual interest */}
        <div className="absolute top-0 right-0 w-3 h-3 bg-gradient-to-br from-teal-400/30 to-transparent"></div>
      </div>

      {/* Title with Enhanced Styling */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-bold text-gray-900 text-sm leading-tight line-clamp-2 group-hover:text-teal-700 transition-colors duration-200 flex-1">
            {item.title || 'Untitled'}
          </div>
          {/* Published Badge */}
          {item.status === 'published' && (
            <div className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              <span className="text-[10px] font-semibold text-green-700">Published</span>
            </div>
          )}
        </div>
        {item.subtitle && (
          <div className="text-xs text-gray-500 mt-1 line-clamp-1 italic">
            {item.subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

interface SuggestedContentTabProps {
  data: SuggestedItem[];
  loading?: boolean;
  error?: string | null;
  onViewItem: (item: SuggestedItem) => void;
  onEdit: (item: SuggestedItem) => void;
  onCloseEdit: () => void;
  onSaveEdit: (item: SuggestedItem, updates: any) => Promise<void>;
  editingItem: SuggestedItem | null;
  isEditModalOpen: boolean;
  onRemove: (item: SuggestedItem) => void;
  onUpdate?: (item: SuggestedItem, updates: Partial<SuggestedItem>) => void;
  onRefetch?: () => Promise<void>;
  onRefetchCalendar?: () => Promise<void>;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  onGlobalSearch?: (query: string) => Promise<void>;
  globalFilterValue?: string;
  statusFilter?: 'draft' | 'published' | 'all';
  onStatusFilterChange?: (status: 'draft' | 'published' | 'all') => void;
  sorting?: Array<{ id: string; desc: boolean }>;
  onSortingChange?: (sorting: Array<{ id: string; desc: boolean }>) => void;
}

/**
 * Marketing-specific wrapper for suggested content
 * Only defines data structure and callbacks, no UI logic
 */
export const SuggestedContentTab: FC<SuggestedContentTabProps> = ({
  data,
  loading = false,
  error: _error = null, // Prefix with _ to indicate intentionally unused
  onViewItem,
  onEdit,
  onCloseEdit,
  onSaveEdit,
  editingItem,
  isEditModalOpen,
  onRemove,
  onUpdate,
  onRefetchCalendar,
  pagination,
  onPaginationChange,
  onGlobalSearch,
  globalFilterValue,
  statusFilter = 'all',
  onStatusFilterChange,
  sorting: externalSorting,
  onSortingChange,
}) => {
  // Internal sorting state management - use ref to track if we're updating internally
  const isInternalUpdateRef = useRef(false);
  // Default to createdAt DESC if no external sorting provided
  const [internalSorting, setInternalSorting] = useState<Array<{ id: string; desc: boolean }>>(
    externalSorting && externalSorting.length > 0
      ? externalSorting
      : [{ id: 'createdAt', desc: true }]
  );

  useEffect(() => {
    if (!isInternalUpdateRef.current && externalSorting !== undefined) {
      setInternalSorting(externalSorting);
    }
    isInternalUpdateRef.current = false;
  }, [externalSorting]);

  const handleSortingChange = useCallback((updater: any) => {
    setInternalSorting((prev) => {
      const current = prev || [];
      const next = typeof updater === 'function' ? updater(current) : updater;

      isInternalUpdateRef.current = true;

      if (onSortingChange) {
        onSortingChange(next);
      }

      return next;
    });
  }, [onSortingChange]);

  const columns: EditableColumn<SuggestedItem>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Post ID',
      editable: false,
      enableSorting: true,
      render: (row: SuggestedItem) => {
        if (!row.id) return <span className="text-gray-400 text-xs italic">N/A</span>;
        return (
          <div className="flex items-center">
            <span className="inline-flex items-center justify-center min-w-[60px] px-2.5 py-1.5 rounded-md font-mono text-xs font-semibold bg-gradient-to-br from-slate-50 to-slate-100 text-slate-700 border border-slate-300 shadow-sm hover:shadow-md hover:from-slate-100 hover:to-slate-200 transition-all duration-200">
              #{row.id}
            </span>
          </div>
        );
      },
    },
    {
      key: 'title',
      header: 'Title',
      editable: true,
      enableSorting: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Title is required';
        return undefined;
      },
      render: (row: SuggestedItem) => <TemplatePreview item={row} />,
    },
    {
      key: 'preview',
      header: 'Preview',
      editable: false,
      enableSorting: false,
      render: (row) => {
        // Only show preview text, not subtitle (subtitle is already in Title column)
        const previewText = row.preview || '';
        return (
          <div className="max-w-[300px]">
            <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
              {previewText || <span className="text-gray-400 italic">No preview available</span>}
            </p>
          </div>
        );
      },
    },
    {
      key: 'template',
      header: 'Category',
      editable: true,
      enableSorting: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Category is required';
        return undefined;
      },
      render: (row) => {
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
              {row.template || 'Unknown'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'dates',
      header: 'Timeline',
      editable: false,
      enableSorting: true,
      render: (row) => {
        const formatDate = (date: Date | string | null | undefined) => {
          if (!date) return null;
          const d = new Date(date);
          return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        };

        const publishDate = row.plannedDate ? formatDate(row.plannedDate) : null;
        const createdDate = row.createdAt ? formatDate(row.createdAt) : null;
        const isPublished = row.status === 'published';

        return (
          <div className="flex flex-col gap-1.5 min-w-[140px]">
            {/* Publish Date */}
            {publishDate ? (
              <div className="flex items-center gap-1.5 group">
                <div className="flex items-center gap-1.5 flex-1">
                  <Calendar className={`w-3.5 h-3.5 ${isPublished ? 'text-green-600' : 'text-teal-600'} shrink-0`} />
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-medium uppercase tracking-wide ${isPublished ? 'text-green-700' : 'text-teal-700'}`}>
                      {isPublished ? 'Published' : 'TO BE PUBLISHED'}
                    </span>
                    <span className="text-xs font-semibold text-gray-900 leading-tight">
                      {publishDate}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 opacity-50">
                <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                    Publish
                  </span>
                  <span className="text-xs text-gray-400 italic">Not scheduled</span>
                </div>
              </div>
            )}

            {/* Created Date */}
            {createdDate && (
              <div className="flex items-center gap-1.5 pt-1 border-t border-gray-100">
                <Clock className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                    Created
                  </span>
                  <span className="text-xs text-gray-600 leading-tight">
                    {createdDate}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      editable: false,
      enableEditing: false,
      enableSorting: false,
      render: (row) => {
        const items: DropdownItem[] = [
          {
            key: 'view',
            label: 'View',
            icon: Eye,
            onClick: () => onViewItem(row),
            variant: 'info',
          },
          {
            key: 'remove',
            label: 'Remove',
            icon: X,
            onClick: () => onRemove(row),
            variant: 'danger',
          },
        ];

        return <Dropdown items={items} />;
      }
    }
  ], [onViewItem, onRemove]);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
  ];

  return (
    <>
      <div className="mb-6 flex items-center justify-end">
        <div className="w-48">
          <Select
            value={statusFilter}
            onChange={(e) => {
              const value = e.target.value as 'draft' | 'published' | 'all';
              if (onStatusFilterChange) {
                onStatusFilterChange(value);
              }
            }}
            options={statusOptions}
            className="w-full text-xs"
          />
        </div>
      </div>
      <EditableDataTable
        title="Suggested Content"
        columns={columns}
        data={data}
        keyExtractor={(row, idx) => `suggested-${row.id || idx}`}
        enableEditing={true}
        enableDelete={true}
        initialEditingMode="cell"
        loading={loading}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        onGlobalSearch={onGlobalSearch}
        globalFilterValue={globalFilterValue}
        sorting={internalSorting}
        onSortingChange={handleSortingChange}
        manualSorting={true}
        onRowUpdate={async (oldRow, newRow) => {
          if (onUpdate) {
            const updates: Partial<SuggestedItem> = {};
            if (newRow.title !== oldRow.title) updates.title = newRow.title;
            if (newRow.template !== oldRow.template) updates.template = newRow.template;
            await onUpdate(oldRow, updates);
          }
        }}
        onRowDelete={async (row) => {
          if (onRemove) {
            await onRemove(row);
          }
        }}
        renderCustomRowActions={(row) => (
          <Button
            onClick={() => onEdit(row)}
            variant="ghost"
            size="sm"
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50"
            title="Edit row"
            aria-label="Edit row"
          >
            <Pencil className="w-4 h-4" />
          </Button>
        )}
      />

      {/* Edit Content Modal */}
      <EditContentModal
        item={editingItem}
        isOpen={isEditModalOpen}
        onClose={onCloseEdit}
        onSave={onSaveEdit}
        onRefetchCalendar={onRefetchCalendar}
      />
    </>
  );
};
